import * as THREE from "three";
import type { CatalogItem, Language } from "../types";
import { detectCompositeCover, selectCaseImageSources } from "../lib/coverLayout";

export class ProductCaseViewer {
  private readonly canvas: HTMLCanvasElement;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(38, 1, 0.1, 20);
  private readonly clock = new THREE.Clock();
  private readonly textures = new Set<THREE.Texture>();
  private renderer: THREE.WebGLRenderer | null = null;
  private caseGroup: THREE.Group | null = null;
  private frame = 0;
  private active = false;
  private destroyed = false;
  private rotationX = -0.085;
  private rotationY = 0.18;
  private caseYOffset = -0.07;
  private shadowScaleX = 0.75;
  private caseXOffsetMobile = -0.05;
  private readonly cameraMobile: [number, number, number] = [-0.48, 0.42, 3.95];
  private pointer: { id: number; x: number; y: number } | null = null;
  private loadToken = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.camera.position.set(-0.48, 0.44, 4.15);
    this.camera.lookAt(0, -0.06, 0);
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.3));
    const key = new THREE.DirectionalLight(0xfff5e7, 2.15);
    key.position.set(-3, 4, 5);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xb9d7ff, 0.95);
    rim.position.set(4, 1, -3);
    this.scene.add(rim);
    this.bindEvents();
  }

  show(item: CatalogItem, language: Language): void {
    this.ensureRenderer();
    this.clearCase();
    this.active = true;
    this.rotationX = -0.085;
    this.rotationY = 0.18;
    const token = ++this.loadToken;
    this.caseGroup = this.buildCase(item, language);
    this.caseGroup.position.y = this.caseYOffset;
    if (window.matchMedia("(max-width: 699px)").matches) {
      this.caseGroup.position.x = this.caseXOffsetMobile;
    }
    this.caseGroup.rotation.set(this.rotationX, this.rotationY, 0);
    this.scene.add(this.caseGroup);
    this.resize();
    void this.loadCaseImages(item).then(({ frontTexture, backTexture, spineTexture }) => {
      if (!frontTexture && !backTexture && !spineTexture) return;
      if (token !== this.loadToken || !this.caseGroup) {
        [frontTexture, backTexture, spineTexture].filter((texture): texture is THREE.Texture => Boolean(texture)).forEach((texture) => {
          texture.dispose();
          this.textures.delete(texture);
        });
        return;
      }
      const caseMesh = this.caseGroup.getObjectByName("rental-case") as THREE.Mesh | undefined;
      if (!caseMesh || !Array.isArray(caseMesh.material)) return;
      if (frontTexture) {
        caseMesh.material[4].dispose();
        caseMesh.material[4] = new THREE.MeshStandardMaterial({ map: frontTexture, roughness: 0.48, metalness: 0.01 });
      }
      if (backTexture) {
        caseMesh.material[5].dispose();
        caseMesh.material[5] = new THREE.MeshStandardMaterial({ map: backTexture, roughness: 0.5, metalness: 0.01 });
      }
      if (spineTexture) {
        // BoxGeometry material 0 is the viewer-right/value-sticker side. Keep
        // its generated label and apply the center band only to the left spine.
        caseMesh.material[1].dispose();
        caseMesh.material[1] = new THREE.MeshStandardMaterial({ map: spineTexture, roughness: 0.5, metalness: 0.01 });
      }
    });
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(this.animate);
  }

  hide(): void {
    this.active = false;
    this.pointer = null;
    cancelAnimationFrame(this.frame);
  }

  destroy(): void {
    this.destroyed = true;
    this.hide();
    this.unbindEvents();
    this.clearCase();
    this.textures.forEach((texture) => texture.dispose());
    this.renderer?.dispose();
  }

  private ensureRenderer(): void {
    if (this.renderer) return;
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true, powerPreference: "high-performance" });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.88;
    this.renderer.setClearColor(0x000000, 0);
  }

  private buildCase(item: CatalogItem, language: Language): THREE.Group {
    const group = new THREE.Group();
    const frontFallback = this.makeCoverFallback(item);
    const backTexture = this.makeBackTexture(item, language);
    const spineTexture = this.makeSpineTexture(item);
    const black = new THREE.MeshStandardMaterial({ color: 0x111315, roughness: 0.58 });
    const side = new THREE.MeshStandardMaterial({ map: spineTexture, roughness: 0.56 });
    const front = new THREE.MeshStandardMaterial({ map: frontFallback, roughness: 0.5 });
    const back = new THREE.MeshStandardMaterial({ map: backTexture, roughness: 0.6 });
    const caseMesh = new THREE.Mesh(
      new THREE.BoxGeometry(1.48, 2.12, 0.17, 2, 2, 1),
      [side, side.clone(), black, black.clone(), front, back],
    );
    caseMesh.name = "rental-case";
    group.add(caseMesh);

    // 上部が空洞のレンタルケースに、上からまっすぐ差し込むプラスチック・カセットケース。
    // 奥行きの中央に置くので前面カバーの裏側に収まり、はみ出た上部だけが見える。
    const cassette = new THREE.Group();

    const cassetteBody = new THREE.Mesh(
      new THREE.BoxGeometry(1.34, 2.0, 0.13),
      new THREE.MeshPhysicalMaterial({
        color: 0xeef2f4,
        transparent: true,
        opacity: 0.72,
        roughness: 0.15,
        metalness: 0,
        clearcoat: 1,
        clearcoatRoughness: 0.04,
      }),
    );
    cassetteBody.position.set(0, 0.18, 0.0);
    cassette.add(cassetteBody);

    // はみ出た上部の横長の黄色い帯（作品識別番号）
    const cassetteLabel = new THREE.Mesh(
      new THREE.PlaneGeometry(1.34, 0.18),
      new THREE.MeshBasicMaterial({ map: this.makeRentalLabel(item), transparent: true, toneMapped: false }),
    );
    cassetteLabel.position.set(0, 1.12, 0.07);
    cassette.add(cassetteLabel);

    group.add(cassette);

    const priceSticker = new THREE.Mesh(
      new THREE.PlaneGeometry(0.264, 0.1032),
      new THREE.MeshBasicMaterial({ map: this.makePriceSticker((item.price.display ?? "—").replace(/[~〜]/g, "")), transparent: true, toneMapped: false }),
    );
    priceSticker.position.set(0.58, 1.0, 0.092);
    priceSticker.rotation.z = -0.035;
    priceSticker.renderOrder = 6;
    group.add(priceSticker);

    const tableShadow = new THREE.Mesh(
      new THREE.CircleGeometry(1.02, 48),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.22, depthWrite: false }),
    );
    tableShadow.rotation.x = -Math.PI / 2;
    tableShadow.scale.set(this.shadowScaleX, 0.32, 1);
    tableShadow.position.set(0, -1.3, -0.1);
    group.add(tableShadow);
    return group;
  }

  private async loadCaseImages(item: CatalogItem): Promise<{
    frontTexture: THREE.Texture | null;
    backTexture: THREE.Texture | null;
    spineTexture: THREE.Texture | null;
  }> {
    const detail = item.images.detail;
    const portrait = item.images.shelfHigh ?? item.images.shelf;
    const hasSpread = detail && portrait
      ? await detectCompositeCover(this.proxyUrl(detail), this.proxyUrl(portrait))
      : false;
    const sources = selectCaseImageSources(item.images, hasSpread);
    if (!hasSpread) {
      let frontTexture: THREE.Texture | null = null;
      for (const source of sources.frontCandidates) {
        frontTexture = await this.loadCaseTexture(source, "plain");
        if (frontTexture) break;
      }
      // When the large package artwork is a single composition, use the first
      // two API samples as the physical front/back instead of inventing crops.
      // FANZA displays these as the second and third product-page images after
      // the unsplittable `imageURL.large` artwork.
      const backTexture = await this.loadCaseTexture(sources.back, "plain");
      return { frontTexture, backTexture, spineTexture: null };
    }
    // A valid package spread is mapped as one source across all three physical
    // faces: left=back, center=spine, right=front.
    const [frontTexture, backTexture, spineTexture] = await Promise.all([
      this.loadCaseTexture(sources.frontCandidates[0] ?? null, "front"),
      this.loadCaseTexture(sources.back, "back"),
      this.loadCaseTexture(sources.spine, "spine"),
    ]);
    return { frontTexture, backTexture, spineTexture };
  }

  private async loadCaseTexture(source: string | null, face: "front" | "back" | "spine" | "plain"): Promise<THREE.Texture | null> {
    if (!source) return null;
    try {
      const texture = await new THREE.TextureLoader().loadAsync(this.proxyUrl(source));
      const image = texture.image as { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number };
      const width = image.naturalWidth ?? image.width ?? 0;
      const height = image.naturalHeight ?? image.height ?? 0;
      if (width <= 0 || height <= 0) {
        texture.dispose();
        return null;
      }
      if (face === "back" && width > height * 1.08) {
        // DMM large is normally a back/spine/front spread. The left 47% keeps
        // the back artwork while trimming the central spine band.
        texture.repeat.set(0.47, 1);
        texture.offset.set(0, 0);
        texture.needsUpdate = true;
      } else if (face === "front" && width > height * 1.08) {
        // The right 47% keeps the front cover from the same spread.
        texture.repeat.set(0.47, 1);
        texture.offset.set(0.53, 0);
        texture.needsUpdate = true;
      } else if (face === "spine" && width > height * 1.08) {
        // The central 6% is the package spine between the two 47% faces.
        texture.repeat.set(0.06, 1);
        texture.offset.set(0.47, 0);
        texture.needsUpdate = true;
      }
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 8;
      this.textures.add(texture);
      return texture;
    } catch {
      return null;
    }
  }

  private makeCoverFallback(item: CatalogItem): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 840;
    const context = canvas.getContext("2d");
    if (context) {
      const hue = [...item.id].reduce((sum, character) => sum + character.charCodeAt(0), 0) % 360;
      const gradient = context.createLinearGradient(0, 0, 600, 840);
      gradient.addColorStop(0, `hsl(${hue} 44% 55%)`);
      gradient.addColorStop(1, `hsl(${(hue + 34) % 360} 58% 18%)`);
      context.fillStyle = gradient;
      context.fillRect(0, 0, 600, 840);
      context.fillStyle = "rgba(0,0,0,.55)";
      context.fillRect(0, 0, 600, 110);
      context.fillStyle = "#fff";
      context.textAlign = "center";
      context.font = "900 72px sans-serif";
      context.fillText("QUIYA", 300, 78);
      context.font = "700 27px sans-serif";
      this.drawWrappedText(context, item.title, 300, 420, 510, 42, 8);
      context.fillStyle = "#f1ca2d";
      context.fillRect(0, 760, 600, 80);
      context.fillStyle = "#171717";
      context.font = "900 28px sans-serif";
      context.fillText(item.id.toUpperCase(), 300, 812);
    }
    return this.registerCanvasTexture(canvas);
  }

  private makeBackTexture(item: CatalogItem, language: Language): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 840;
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = "#17191a";
      context.fillRect(0, 0, 600, 840);
      context.fillStyle = "#a41d31";
      context.fillRect(0, 0, 600, 92);
      context.fillStyle = "#fff";
      context.font = "900 34px sans-serif";
      context.textAlign = "left";
      context.fillText("QUIYA RENTAL VIDEO", 36, 58);
      context.font = "800 27px sans-serif";
      this.drawWrappedText(context, item.title, 38, 142, 520, 38, 6);
      const labels = language === "ja"
        ? [["出演", item.performers.join(" / ") || "—"], ["メーカー", item.maker ?? "—"], ["発売日", item.releaseDate ?? "—"], ["ジャンル", item.genres.slice(0, 5).map((genre) => genre.name).join(" / ") || "—"]]
        : [["CAST", item.performers.join(" / ") || "—"], ["MAKER", item.maker ?? "—"], ["RELEASE", item.releaseDate ?? "—"], ["GENRES", item.genres.slice(0, 5).map((genre) => genre.name).join(" / ") || "—"]];
      let y = 430;
      labels.forEach(([label, value]) => {
        context.fillStyle = "#e9c633";
        context.font = "800 20px sans-serif";
        context.fillText(label, 38, y);
        context.fillStyle = "#e8e8e3";
        context.font = "600 20px sans-serif";
        this.drawWrappedText(context, value, 152, y, 400, 28, 2);
        context.fillStyle = "rgba(255,255,255,.12)";
        context.fillRect(38, y + 42, 524, 2);
        y += 82;
      });
      this.drawBarcode(context, 42, 756, 350, 52, item.id);
      context.fillStyle = "#f1f1ec";
      context.font = "800 21px monospace";
      context.fillText(item.id.toUpperCase(), 414, 792);
    }
    return this.registerCanvasTexture(canvas);
  }

  private makeSpineTexture(item: CatalogItem): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width = 150;
    canvas.height = 840;
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = "#101214";
      context.fillRect(0, 0, 150, 840);
      context.fillStyle = "#e6bd28";
      context.fillRect(0, 0, 22, 840);
      context.save();
      context.translate(94, 770);
      context.rotate(-Math.PI / 2);
      context.fillStyle = "#fff";
      context.font = "800 26px sans-serif";
      context.textAlign = "left";
      context.fillText(item.title.slice(0, 42), 0, 0, 690);
      context.restore();
    }
    return this.registerCanvasTexture(canvas);
  }

  private makeRentalLabel(item: CatalogItem): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width = 800;
    canvas.height = 100;
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = "#f4d128";
      context.fillRect(0, 0, 800, 100);
      context.fillStyle = "#111";
      context.fillRect(0, 0, 170, 100);
      context.fillStyle = "#f4d128";
      context.font = "900 56px sans-serif";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText("DVD", 85, 50);
      context.fillStyle = "#111";
      context.font = "800 30px sans-serif";
      context.fillText("QUIYA", 390, 50);
      context.font = "700 27px monospace";
      context.fillText(item.id.toUpperCase().slice(-12), 675, 50);
    }
    return this.registerCanvasTexture(canvas);
  }

  private makePriceSticker(priceDisplay: string): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 200;
    const context = canvas.getContext("2d");
    if (context) {
      const w = canvas.width;
      const h = canvas.height;
      const radius = 22;
      context.clearRect(0, 0, w, h);

      // 不透明に近い、黄ばんだシールの台紙
      context.save();
      this.roundedRect(context, 8, 8, w - 16, h - 16, radius);
      const backing = context.createLinearGradient(0, 0, w, h);
      backing.addColorStop(0, "rgba(248,245,233,0.94)");
      backing.addColorStop(1, "rgba(234,227,208,0.88)");
      context.fillStyle = backing;
      context.fill();
      context.restore();

      // 端の汚れと年代くすみ
      context.save();
      this.roundedRect(context, 8, 8, w - 16, h - 16, radius);
      context.clip();
      for (let i = 0; i < 70; i++) {
        const ex = Math.random() * w;
        const ey = Math.random() * h;
        const edge = Math.min(ex, w - ex, ey, h - ey);
        if (edge < 26) {
          context.fillStyle = `rgba(96,76,40,${0.05 + Math.random() * 0.09})`;
          context.beginPath();
          context.arc(ex, ey, 4 + Math.random() * 16, 0, Math.PI * 2);
          context.fill();
        }
      }
      context.restore();

      // 紙・プラのざらつき
      for (let i = 0; i < 2000; i++) {
        const gx = Math.random() * w;
        const gy = Math.random() * h;
        context.fillStyle = Math.random() > 0.5
          ? `rgba(120,110,90,${0.04 + Math.random() * 0.06})`
          : `rgba(255,255,255,${0.04 + Math.random() * 0.05})`;
        context.fillRect(gx, gy, 1.2, 1.2);
      }

      // 黒の細字でかすれた価格文字
      context.save();
      this.roundedRect(context, 8, 8, w - 16, h - 16, radius);
      context.clip();
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillStyle = "rgba(18,18,18,0.92)";
      let priceFont = 132;
      const maxPriceWidth = w - 64;
      context.font = `300 ${priceFont}px 'Helvetica Neue', Arial, sans-serif`;
      while (context.measureText(priceDisplay).width > maxPriceWidth && priceFont > 64) {
        priceFont -= 4;
        context.font = `300 ${priceFont}px 'Helvetica Neue', Arial, sans-serif`;
      }
      context.fillText(priceDisplay, w / 2, h / 2 + 4);
      context.globalCompositeOperation = "destination-out";
      for (let i = 0; i < 60; i++) {
        context.fillStyle = `rgba(0,0,0,${0.06 + Math.random() * 0.16})`;
        context.fillRect(Math.random() * w, Math.random() * h, 1 + Math.random() * 3, 1 + Math.random() * 10);
      }
      context.globalCompositeOperation = "source-over";
      context.restore();

      // 薄く退色したバーコード
      context.save();
      this.roundedRect(context, 8, 8, w - 16, h - 16, radius);
      context.clip();
      const bx = w * 0.52;
      const by = h - 20;
      const bw = w * 0.4;
      const bh = 12;
      const seed = "RENTAL250";
      let cursor = bx;
      let index = 0;
      while (cursor < bx + bw) {
        const value = seed.charCodeAt(index % seed.length);
        const bar = 1 + (value % 3);
        context.fillStyle = `rgba(40,35,30,${0.4 + Math.random() * 0.22})`;
        context.fillRect(cursor, by, bar, bh);
        cursor += bar + 2 + (value % 2);
        index += 1;
      }
      context.restore();

      // プラスチックの光沢
      context.save();
      this.roundedRect(context, 8, 8, w - 16, h - 16, radius);
      context.clip();
      const sheen = context.createLinearGradient(0, 0, w, h);
      sheen.addColorStop(0, "rgba(255,255,255,0)");
      sheen.addColorStop(0.45, "rgba(255,255,255,0.1)");
      sheen.addColorStop(0.55, "rgba(255,255,255,0.1)");
      sheen.addColorStop(1, "rgba(255,255,255,0)");
      context.fillStyle = sheen;
      context.fillRect(0, 0, w, h);
      context.restore();

      // めくれた角
      context.save();
      context.beginPath();
      context.moveTo(w - 38, 8);
      context.lineTo(w - 8, 8);
      context.lineTo(w - 8, 38);
      context.closePath();
      context.fillStyle = "rgba(20,20,20,0.16)";
      context.fill();
      context.beginPath();
      context.moveTo(w - 30, 8);
      context.lineTo(w - 8, 8);
      context.lineTo(w - 8, 30);
      context.closePath();
      context.fillStyle = "rgba(255,255,255,0.34)";
      context.fill();
      context.restore();
    }
    return this.registerCanvasTexture(canvas);
  }

  private roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    context.beginPath();
    context.moveTo(x + radius, y);
    context.arcTo(x + width, y, x + width, y + height, radius);
    context.arcTo(x + width, y + height, x, y + height, radius);
    context.arcTo(x, y + height, x, y, radius);
    context.arcTo(x, y, x + width, y, radius);
    context.closePath();
  }

  private drawWrappedText(context: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number, maxLines: number): void {
    const characters = [...text];
    let line = "";
    let lineIndex = 0;
    for (const character of characters) {
      if (context.measureText(line + character).width > maxWidth && line) {
        context.fillText(line, x, y + lineIndex * lineHeight);
        line = character;
        lineIndex += 1;
        if (lineIndex >= maxLines) return;
      } else {
        line += character;
      }
    }
    if (lineIndex < maxLines) context.fillText(line, x, y + lineIndex * lineHeight);
  }

  private drawBarcode(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, seed: string): void {
    context.fillStyle = "#fff";
    context.fillRect(x, y, width, height);
    let cursor = x + 8;
    let index = 0;
    while (cursor < x + width - 8) {
      const value = seed.charCodeAt(index % seed.length);
      const bar = 1 + (value % 4);
      context.fillStyle = "#111";
      context.fillRect(cursor, y + 4, bar, height - 8);
      cursor += bar + 2 + (value % 2);
      index += 1;
    }
  }

  private registerCanvasTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;
    this.textures.add(texture);
    return texture;
  }

  private proxyUrl(source: string): string {
    try {
      const url = new URL(source, window.location.href);
      if (url.hostname === "awsimgsrc.dmm.co.jp" || url.hostname === "pics.dmm.co.jp") {
        return `/api/image?v=5&src=${encodeURIComponent(url.toString())}`;
      }
    } catch {
      return source;
    }
    return source;
  }

  private clearCase(): void {
    if (this.caseGroup) {
      this.scene.remove(this.caseGroup);
      this.caseGroup.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        (Array.isArray(object.material) ? object.material : [object.material]).forEach((material) => material.dispose());
      });
      this.caseGroup = null;
    }
    this.textures.forEach((texture) => texture.dispose());
    this.textures.clear();
  }

  private bindEvents(): void {
    window.addEventListener("resize", this.resize);
    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    this.canvas.addEventListener("pointermove", this.onPointerMove);
    this.canvas.addEventListener("pointerup", this.onPointerUp);
    this.canvas.addEventListener("pointercancel", this.onPointerUp);
  }

  private unbindEvents(): void {
    window.removeEventListener("resize", this.resize);
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerUp);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    event.stopPropagation();
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.classList.add("is-dragging");
    this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.pointer || this.pointer.id !== event.pointerId) return;
    const dx = event.clientX - this.pointer.x;
    const dy = event.clientY - this.pointer.y;
    this.pointer.x = event.clientX;
    this.pointer.y = event.clientY;
    this.rotationY += dx * 0.012;
    this.rotationX = THREE.MathUtils.clamp(this.rotationX + dy * 0.006, -0.42, 0.42);
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.pointer && this.pointer.id !== event.pointerId) return;
    this.canvas.classList.remove("is-dragging");
    this.pointer = null;
  };

  private readonly resize = (): void => {
    if (!this.renderer) return;
    const isMobile = window.matchMedia("(max-width: 699px)").matches;
    if (isMobile) {
      this.camera.position.set(...this.cameraMobile);
    } else {
      this.camera.position.set(-0.48, 0.42, 4.15);
    }
    const width = this.canvas.clientWidth || 320;
    const height = this.canvas.clientHeight || 440;
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.updateProjectionMatrix();
    // The case viewer shows a single object, so it is cheap to render. Match the
    // device's native density (capped at 3) so the jacket stays crisp on
    // high-DPR phones instead of being upscaled from a 1.6x framebuffer.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 3));
    this.renderer.setSize(width, height, false);
  };

  private readonly animate = (): void => {
    if (!this.active || this.destroyed || !this.renderer) return;
    this.clock.getDelta();
    if (this.caseGroup) {
      this.caseGroup.rotation.x += (this.rotationX - this.caseGroup.rotation.x) * 0.16;
      this.caseGroup.rotation.y += (this.rotationY - this.caseGroup.rotation.y) * 0.16;
    }
    this.renderer.render(this.scene, this.camera);
    this.frame = requestAnimationFrame(this.animate);
  };
}
